<script lang="ts" module>
	type Size = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

	const sizes: Record<Size, string> = {
		xs: "w-6 h-6",
		sm: "w-8 h-8",
		md: "w-10 h-10",
		lg: "w-20 h-20",
		xl: "w-36 h-36",
		xxl: "w-48 h-48",
	};
	const pixelSizes: Record<Size, number> = {
		xs: 24,
		sm: 32,
		md: 40,
		lg: 80,
		xl: 144,
		xxl: 192,
	};
</script>

<script lang="ts">
	import { twMerge } from "tailwind-merge";
	import type { Point } from "#gen/bundles/v1/common_pb.js";
	import FocalImage from "#lib/components/FocalImage.svelte";
	import type { Snippet } from "svelte";
	import type { HTMLAnchorAttributes, HTMLAttributes } from "svelte/elements";

	export type AvatarProps = {
		href?: string;
		src?: string;
		rounded?: boolean;
		border?: boolean;
		stacked?: boolean;
		alt?: string;
		size?: Size;
		priority?: boolean;
		focalPoint?: Point;
		class?: string;
		children?: Snippet;
	} & (HTMLAnchorAttributes | HTMLAttributes<HTMLDivElement>);

	const { src = "", href, rounded = true, border = false, stacked = false, alt = "", size = "md", priority = false, focalPoint, class: className, children, ...other }: AvatarProps = $props();
	const avatarClass = $derived(twMerge(rounded ? "rounded-full" : "rounded-none", border && "p-1 ring-2 ring-gray-300 dark:ring-gray-500", sizes[size], stacked && "border-2 -ms-4 border-white dark:border-gray-800", "overflow-clip bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 object-cover", className));
</script>

{#snippet placeholder()}
	<svg class="text-gray-300" width={pixelSizes[size]} height={pixelSizes[size]} fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
		<defs>
			<clipPath id="avatar-fallback-circle-clip">
				<circle cx="8" cy="8" r="8" />
			</clipPath>
		</defs>
		<g clip-path={rounded ? `url(#avatar-fallback-circle-clip)` : undefined}>
			<path fill-rule="evenodd" d="M8 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
		</g>
	</svg>
{/snippet}

<svelte:element this={href ? "a" : "div"} {href} {...other} class={twMerge("relative flex items-center justify-center", avatarClass)}>
	{#if !children}
		<FocalImage {priority} height={pixelSizes[size]} width={pixelSizes[size]} layout="fixed" {alt} {src} {focalPoint} placeholderSnippet={placeholder} />
	{:else}
		{@render children()}
	{/if}
</svelte:element>
